/* Best-Preis: pinned UI behaviour - the bottom buy bar and the search button.

   Three jobs:

   1. Keep --bp-dock-h truthful. Every floating element positions itself off that
      one number, so measuring it correctly is what makes overlap impossible. It
      is measured, never assumed, and re-measured whenever the bar resizes.

   2. Drive the buy bar. The bar owns no cart logic at all: its button forwards
      the click to the real add-to-cart button on the page. That is deliberate -
      it means the defect acknowledgement gate (Sec. 434 BGB), the zero-price
      block, variant selection, quantity and Horizon's cart drawer all keep
      working exactly as they do without the bar. A second implementation would
      be a second thing to get wrong, and the gate is not a thing to get wrong.

   3. Drive the search button, which opens Horizon's own search modal rather
      than a second search of our own.

   Desktop note: Horizon makes .page-wrapper the scroll container, so window
   scroll events do not fire there. Everything here uses IntersectionObserver
   against the viewport instead, which is correct under both layouts.
*/
(function () {
  'use strict';

  var root = document.documentElement;
  var dock = document.querySelector('[data-bp-dock]');
  var fab = document.querySelector('[data-bp-fab]');

  /* ------------------------------------------------------------------ *
   * 1. The dock height contract
   * ------------------------------------------------------------------ */

  function setDockHeight(px) {
    var v = Math.max(0, Math.round(px || 0));
    root.style.setProperty('--bp-dock-h', v + 'px');
  }

  function measureDock() {
    if (!dock || !dock.classList.contains('is-on')) return setDockHeight(0);
    var rect = dock.getBoundingClientRect();
    setDockHeight(rect && rect.height);
  }

  if (dock) {
    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(measureDock).observe(dock);
    }
    // The bar is shown and hidden by toggling .is-on, and a hidden bar must
    // report zero so nothing reserves space for furniture that is not there.
    if (typeof MutationObserver === 'function') {
      new MutationObserver(measureDock).observe(dock, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }
    addEventListener('resize', measureDock);
    addEventListener('orientationchange', measureDock);
    measureDock();
  }

  /* ------------------------------------------------------------------ *
   * 2. The buy bar
   * ------------------------------------------------------------------ */

  // Ordered most specific first. Horizon's own markup may change between
  // versions, so several shapes are accepted rather than one brittle selector.
  var CTA_SELECTORS = [
    'product-form-component button[type="submit"]:not([disabled])',
    'product-form-component .add-to-cart-button',
    '.add-to-cart-button',
    '[data-testid="standalone-add-to-cart"]',
    'form[action*="/cart/add"] button[type="submit"]',
  ];

  function findRealCta() {
    for (var i = 0; i < CTA_SELECTORS.length; i++) {
      var el = document.querySelector(CTA_SELECTORS[i]);
      if (el) return el;
    }
    return null;
  }

  function ctaDisabled(el) {
    return !!(el.disabled || el.getAttribute('aria-disabled') === 'true');
  }

  function initBuyBar() {
    if (!dock) return;
    var proxy = dock.querySelector('[data-bp-dock-cta]');
    var real = findRealCta();

    // No real button to forward to means the bar would be decoration. Leave it
    // hidden rather than shipping a control that does nothing.
    if (!proxy || !real) return;

    proxy.addEventListener('click', function (e) {
      e.preventDefault();
      if (ctaDisabled(real)) return;
      // Forwarding a real click means every listener already bound to the real
      // button - the defect gate among them - runs untouched.
      real.click();
    });

    function syncDisabled() {
      var off = ctaDisabled(real);
      proxy.disabled = off;
      proxy.setAttribute('aria-disabled', off ? 'true' : 'false');
    }

    if (typeof MutationObserver === 'function') {
      new MutationObserver(syncDisabled).observe(real, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled'],
      });
    }
    syncDisabled();

    if (typeof IntersectionObserver !== 'function') return;
    new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          dock.classList.toggle('is-on', !entries[i].isIntersecting);
        }
        measureDock();
      },
      { rootMargin: '0px', threshold: 0 }
    ).observe(real);

    mirrorPrice();
  }

  /* A product with one variant has one price, and Liquid already rendered it.
     With several variants the price on the bar has to follow the customer's
     choice, so it mirrors the price element on the page. If that element cannot
     be found, the price is removed from the bar rather than left to go stale -
     a wrong price on a buy button is worse than no price at all. */
  function mirrorPrice() {
    var slot = dock.querySelector('[data-bp-dock-price]');
    if (!slot) return;
    if ((dock.getAttribute('data-bp-variants') || '1') === '1') return;

    var candidates = [
      '.product-details__price',
      'product-price',
      '.price__container',
      '.price',
    ];
    var source = null;
    for (var i = 0; i < candidates.length && !source; i++) {
      var found = document.querySelectorAll(candidates[i]);
      for (var j = 0; j < found.length; j++) {
        if (dock.contains(found[j])) continue;
        if (/\d/.test(found[j].textContent || '')) {
          source = found[j];
          break;
        }
      }
    }

    if (!source) {
      // No trustworthy price to follow. Drop the price and, with it, the tax
      // note that only has meaning next to a price (PAngV).
      slot.remove();
      var tax = dock.querySelector('[data-bp-dock-tax]');
      if (tax) tax.remove();
      return;
    }

    function copy() {
      slot.textContent = (source.textContent || '').trim();
    }
    copy();
    if (typeof MutationObserver === 'function') {
      new MutationObserver(copy).observe(source, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }
  }

  /* ------------------------------------------------------------------ *
   * 3. The search button
   * ------------------------------------------------------------------ */

  function openSearch() {
    var modal = document.getElementById('search-modal');

    // Horizon's dialog-component exposes showDialog(); prefer it so the theme's
    // own focus handling and animations run.
    if (modal && typeof modal.showDialog === 'function') {
      modal.showDialog();
      return true;
    }

    var dialog = modal ? modal.querySelector('dialog') : null;
    if (dialog && typeof dialog.showModal === 'function') {
      try {
        if (!dialog.open) dialog.showModal();
        var input = dialog.querySelector('input[type="search"], .search-input');
        if (input) input.focus();
        return true;
      } catch (err) {
        /* falls through to the plain search page */
      }
    }

    // Last resort: the search page always exists, so the button is never dead.
    var url = (fab && fab.getAttribute('data-bp-search-url')) || '/search';
    if (typeof window.location.assign === 'function') window.location.assign(url);
    else window.location.href = url;
    return false;
  }

  function initFab() {
    if (!fab) return;
    fab.addEventListener('click', function (e) {
      e.preventDefault();
      openSearch();
    });

    // The header carries Horizon's own search field. The floating button is for
    // when that has scrolled away, so the two are never offered side by side.
    var header = document.getElementById('header-group') || document.querySelector('header');
    if (!header || typeof IntersectionObserver !== 'function') {
      fab.classList.add('is-on');
      return;
    }
    new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          fab.classList.toggle('is-on', !entries[i].isIntersecting);
        }
      },
      { threshold: 0 }
    ).observe(header);
  }

  /* ------------------------------------------------------------------ *
   * 4. Fast add to cart
   * ------------------------------------------------------------------ */

  /* The button only ever exists for an article that is allowed a one-click add -
     single variant, priced, and not graded defekt. That rule is enforced where
     the button is rendered (bp-grid.liquid, and product-card.liquid for
     Horizon's own quick-add), so a defective article can never reach the cart
     without the Sec. 434 BGB acknowledgement on the product page.

     This uses the same request-and-morph shape as the upsell rail. The two are
     deliberately still separate: that rail is a larger module with its own
     lifecycle, and merging them is a refactor rather than part of this change. */

  function cartSectionIds() {
    var ids = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach(function (c) {
      var id = c.getAttribute('data-section-id');
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  }

  // Horizon's cart listens for `cart:lines-update` and morphs its own section
  // from `event.promise`, so handing it the sections we already fetched avoids a
  // reload and reuses its row-add animation.
  function notifyCart(sections) {
    var resolve;
    var promise = new Promise(function (r) {
      resolve = r;
    });
    var ev = new Event('cart:lines-update', { bubbles: true, cancelable: false });
    ev.promise = promise;
    document.dispatchEvent(ev);
    resolve({ detail: { sections: sections || {} } });
    return promise;
  }

  function fastAdd(btn) {
    var id = Number(btn.getAttribute('data-bp-variant'));
    if (!id) return Promise.resolve(false);

    btn.disabled = true;
    btn.classList.add('is-busy');

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: id, quantity: 1 }],
        sections: cartSectionIds().join(','),
        sections_url: window.location.pathname,
      }),
    })
      .then(function (r) {
        if (!r.ok) throw new Error('cart/add rejected');
        return r.json();
      })
      .then(function (data) {
        btn.classList.remove('is-busy');
        // Most stock here is a single piece, so the button stays spent rather
        // than inviting a second add that would fail at checkout.
        btn.classList.add('is-added');
        return notifyCart(data.sections).then(function () {
          return true;
        });
      })
      .catch(function (err) {
        console.error('[bp-ui] fast add failed', err);
        btn.classList.remove('is-busy');
        btn.disabled = false;
        return false;
      });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('[data-bp-fast-add]') : null;
    if (!btn || btn.disabled) return;
    // The card-wide link sits underneath; stop it taking the click.
    e.preventDefault();
    e.stopPropagation();
    fastAdd(btn);
  });

  initBuyBar();
  initFab();

  // Small surface for the test harness; harmless in the browser.
  window.bpUI = {
    measureDock: measureDock,
    openSearch: openSearch,
    findRealCta: findRealCta,
    fastAdd: fastAdd,
  };
})();
